// src/pages/Dashboard/PresidenteDashboardPage.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Snackbar,
  Backdrop
} from '@mui/material';
import {
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  LogoutOutlined,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  SupervisorAccount as SupervisorAccountIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

import PresidenteDashboardService from '../../services/PresidenteDashboardService';
import MonografiaList from '../../components/Monografia/MonografiaList';
import DefesaList from '../../components/Defesa/DefesaList';
import AvaliacaoList from '../../components/Avaliacao/AvaliacaoList';
import AvaliacoesPendentes from '../../components/Avaliacao/AvaliacoesPendentes';
import Footer from '../../components/Footer/Footer';


import useAuth from '../../hooks/useAuth';
import { useRole } from '../../contexts/RoleContext';

// COMPONENTE DE NOTIFICAÇÃO CUSTOMIZADO
const NotificationSnackbar = ({ open, message, severity, onClose }) => {
  const getIcon = () => {
    switch (severity) {
      case 'success': return <CheckCircleIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        sx={{ 
          width: '100%',
          minWidth: '300px',
          fontSize: '14px',
          '& .MuiAlert-icon': {
            fontSize: '20px'
          }
        }}
        icon={getIcon()}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// PropTypes para NotificationSnackbar
NotificationSnackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  onClose: PropTypes.func.isRequired
};

function PresidenteDashboardPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    monografias: [],
    defesas: [],
    avaliacoes: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // ESTADOS PARA NOTIFICAÇÕES E LOADING
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [submittingAction, setSubmittingAction] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { currentRole } = useRole();
const effectiveRole = currentRole && currentRole !== 'auto' ? currentRole : (user?.tipo || '');

  // FUNÇÃO PARA MOSTRAR NOTIFICAÇÕES
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // FUNÇÃO PARA FECHAR NOTIFICAÇÕES
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // FUNÇÕES DE DOWNLOAD E VISUALIZAÇÃO DE DOCUMENTOS
  const handleDownloadDocument = async (documento, tipo) => {
    try {
      showNotification('🔄 Iniciando download do documento...', 'info');
      
      let fileUrl = null;
      let fileName = null;

      if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
        fileName = `monografia_${documento.id}_${(documento.titulo || documento.projeto?.proposta?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'defesa') {
        fileUrl = documento.documento_defesa || documento.arquivo_url;
        fileName = `defesa_${documento.id}_${(documento.titulo || documento.monografia?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'avaliacao') {
        fileUrl = documento.documento_avaliacao || documento.arquivo_url;
        fileName = `avaliacao_${documento.id}_${documento.titulo?.substring(0, 20) || 'documento'}`;
      }

      if (!fileUrl) {
        showNotification('❌ Documento não encontrado para download.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const apiUrl = `${baseUrl}/api/documentos/${fileNameFromPath}`;

      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('❌ Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      showNotification('⏳ Processando download...', 'info');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        showNotification('❌ Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('Arquivo vazio recebido do servidor.');
      }

      if (blob.type && (blob.type.includes('text/html') || blob.type.includes('application/json'))) {
        throw new Error('Servidor retornou dados incorretos em vez do arquivo.');
      }

      const originalExtension = fileNameFromPath.split('.').pop();
      const finalFileName = `${fileName}.${originalExtension}`;

      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = finalFileName;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 100);

      showNotification(`✅ Download concluído: ${finalFileName}`, 'success');

    } catch (error) {
      console.error('❌ Erro geral no download:', error);
      showNotification(`❌ Erro ao baixar documento: ${error.message}`, 'error');
    }
  };

  const handleViewDocument = async (documento, tipo) => {
    try {
      showNotification('🔍 Abrindo documento...', 'info');
      
      let fileUrl = null;
      
      if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
      } else if (tipo === 'defesa') {
        fileUrl = documento.documento_defesa || documento.arquivo_url;
      } else if (tipo === 'avaliacao') {
        fileUrl = documento.documento_avaliacao || documento.arquivo_url;
      }

      if (!fileUrl) {
        showNotification('❌ Documento não disponível para visualização.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
      const fileExtension = fileNameFromPath.split('.').pop().toLowerCase();
      const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const publicUrl = `${baseUrl}/api/documentos/view/${fileNameFromPath}`;

      if (fileExtension === 'pdf') {
        window.open(publicUrl, '_blank');
        showNotification('📄 Documento PDF aberto em nova aba!', 'success');
      } else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(fileExtension)) {
        // eslint-disable-next-line no-restricted-globals
        const userChoice = window.confirm(
          `📄 Documento: ${fileNameFromPath}\n\n` +
          `Os documentos Word/Excel não podem ser visualizados diretamente no navegador.\n\n` +
          `Clique OK para baixar e abrir no seu computador.\n` +
          `Clique Cancelar para tentar visualizar online (pode não funcionar).`
        );
        
        if (userChoice) {
          handleDownloadDocument(documento, tipo);
        } else {
          const googleViewerUrl = `https://docs.google.com/gviewer?url=${encodeURIComponent(publicUrl)}&embedded=true`;
          window.open(googleViewerUrl, '_blank', 'width=1200,height=800');
          showNotification('🌐 Tentando visualizar documento online...', 'info');
        }
      } else {
        window.open(publicUrl, '_blank');
        showNotification('📄 Documento aberto em nova aba!', 'success');
      }

    } catch (error) {
      console.error('❌ Erro ao visualizar documento:', error);
      showNotification(`❌ Erro ao abrir documento: ${error.message}`, 'error');
    }
  };

  /**
   * Carrega dados do presidente (monografias, defesas dele, avaliações)
   */
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      showNotification('🔄 Carregando dados do painel...', 'info');
      
      const data = await PresidenteDashboardService.getDadosPresidente();
      setDashboardData(data);
      setError(null);
      showNotification('✅ Dados carregados com sucesso!', 'success');
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard (presidente):', err);
      setError('Não foi possível carregar os dados do dashboard.');
      showNotification('❌ Erro ao carregar dados. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  if (effectiveRole === 'presidente') {
    fetchDashboardData();
  }
}, [effectiveRole]);


  const handleRefresh = async () => {
    setRefreshing(true);
    showNotification('🔄 Atualizando dados...', 'info');
    await fetchDashboardData();
    setRefreshing(false);
  };

  const handleLogout = () => {
    showNotification('👋 Encerrando sessão...', 'info');
    logout();
    navigate('/login');
  };

  // Cartões de resumo melhorados
  const renderSummaryCards = () => {
    const summaryData = [
      {
        title: 'Monografias para Revisar',
        value: dashboardData.monografias.filter(m => m.status === 'PENDENTE').length,
        total: dashboardData.monografias.length,
        icon: <DescriptionIcon />,
        color: '#1976d2'
      },
      {
        title: 'Defesas Pendentes',
        value: dashboardData.defesas.filter(d => d.status === 'PENDENTE').length,
        total: dashboardData.defesas.length,
        icon: <SchoolIcon />,
        color: '#2e7d32'
      },
      {
        title: 'Avaliações a Realizar',
        value: dashboardData.avaliacoes.filter(a => a.status === 'PENDENTE').length,
        total: dashboardData.avaliacoes.length,
        icon: <AssessmentIcon />,
        color: '#ed6c02'
      }
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${item.color}20`,
                      color: item.color,
                      mr: 2
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" color="text.secondary">
                    {item.title}
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" color="primary">
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  de {item.total} total
                </Typography>
                {item.total > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={(item.value / item.total) * 100}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    color={item.value === 0 ? 'success' : 'warning'}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderNextDefesas = () => {
    // Filtra defesas cuja data_defesa seja futura
    const proximasDefesas = dashboardData.defesas
      .filter(defesa => new Date(defesa.dataDefesa) > new Date())
      .sort((a, b) => new Date(a.dataDefesa) - new Date(b.dataDefesa))
      .slice(0, 3);

    if (proximasDefesas.length === 0) return null;

    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📅 Próximas Defesas
          <Chip size="small" label={proximasDefesas.length} color="primary" />
        </Typography>
        <Grid container spacing={2}>
          {proximasDefesas.map((defesa, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card
                sx={{
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <CardContent>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    {format(new Date(defesa.dataDefesa), "dd 'de' MMMM', às' HH:mm", { locale: ptBR })}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                    {defesa.titulo || 'Título da defesa não definido'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Chip
                      size="small"
                      label={defesa.status}
                      color={defesa.status === 'PENDENTE' ? 'warning' : 'success'}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(defesa, 'defesa')}
                        disabled={!defesa.documento_defesa && !defesa.arquivo_url}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadDocument(defesa, 'defesa')}
                        disabled={!defesa.documento_defesa && !defesa.arquivo_url}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  // Renderizações das abas MELHORADAS
  const renderMonografias = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">📚 Monografias para Revisão</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                showNotification('🔄 Atualizando monografias...', 'info');
                fetchDashboardData();
              }}
            >
              Atualizar
            </Button>
          </Box>
        </Box>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Como presidente, você tem a responsabilidade de revisar e aprovar as monografias submetidas.
          </Typography>
        </Alert>

        <MonografiaList
          data={dashboardData.monografias}
          onUpdate={fetchDashboardData}
          userRole="presidente"
          onViewDocument={(item) => handleViewDocument(item, 'monografia')}
          onDownloadDocument={(item) => handleDownloadDocument(item, 'monografia')}
          showNotification={showNotification}
        />
      </CardContent>
    </Card>
  );

  const renderDefesas = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">🎓 Defesas</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                showNotification('🔄 Atualizando defesas...', 'info');
                fetchDashboardData();
              }}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Acompanhe e gerencie as defesas de monografias que você presidirá.
          </Typography>
        </Alert>

        <DefesaList
          data={dashboardData.defesas}
          onUpdate={fetchDashboardData}
          userRole="presidente"
          onViewDocument={(item) => handleViewDocument(item, 'defesa')}
          onDownloadDocument={(item) => handleDownloadDocument(item, 'defesa')}
          showNotification={showNotification}
        />
      </CardContent>
    </Card>
  );

  const renderAvaliacoes = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">📊 Avaliações</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                showNotification('🔄 Atualizando avaliações...', 'info');
                fetchDashboardData();
              }}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Gerencie as avaliações das defesas como presidente do Juri examinadora.
          </Typography>
        </Alert>

        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ⏰ Avaliações Pendentes
        </Typography>
        <AvaliacoesPendentes 
          userRole="presidente"
          showNotification={showNotification}
        />

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📋 Todas as Avaliações
        </Typography>
        <AvaliacaoList
          data={dashboardData.avaliacoes}
          onUpdate={fetchDashboardData}
          userRole="presidente"
          onViewDocument={(item) => handleViewDocument(item, 'avaliacao')}
          onDownloadDocument={(item) => handleDownloadDocument(item, 'avaliacao')}
          showNotification={showNotification}
        />
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={50} />
          <Typography variant="h6" color="text.secondary">
            Carregando painel do presidente...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupervisorAccountIcon color="primary" fontSize="large" />
            Painel do Presidente
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Bem-vindo, {user?.nome}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Atualizar dados">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sair">
            <Button
              variant="outlined"
              color="error"
              onClick={handleLogout}
              startIcon={<LogoutOutlined />}
            >
              Sair
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Mensagem de erro, se houver */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Tentar Novamente
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Indicador de refresh */}
      {refreshing && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Atualizando dados...
          </Typography>
        </Box>
      )}

      {/* Cartões de resumo */}
      {renderSummaryCards()}

      {/* Próximas defesas */}
      {renderNextDefesas()}

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          <Tab label="Monografias" icon={<DescriptionIcon />} iconPosition="start" />
          <Tab label="Defesas" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="Avaliações" icon={<AssessmentIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 0 }}>
          {/* ABA 1: Monografias */}
          {activeTab === 0 && renderMonografias()}

          {/* ABA 2: Defesas */}
          {activeTab === 1 && renderDefesas()}

          {/* ABA 3: Avaliações */}
          {activeTab === 2 && renderAvaliacoes()}
        </Box>
      </Paper>

      {/* BACKDROP PARA LOADING DE OPERAÇÕES */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={submittingAction}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">
            Processando solicitação...
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 300 }}>
            Por favor, aguarde enquanto processamos sua solicitação.
          </Typography>
        </Box>
      </Backdrop>

      {/* SISTEMA DE NOTIFICAÇÕES */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />

      <Footer />
    </Box>
  );
}

export default PresidenteDashboardPage;